import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { CurrencyInput } from 'ui-components/components/currency-input';
import { FlexColumn, Gap } from 'ui-components/components/flex';
import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { Input, version2 } from 'ui-components/components/input';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';
import { v4 } from 'uuid';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { NOTIFICATION_TYPE_ERROR, NotificationService } from 'ui-components/services/notification-service';

import { ModalPortal } from '../../../../../../components/modal-portal';
import Global from '../../../../../../store/global-selectors';
import { updateSupplierProduct } from '../../../../../../pages/products-page/suppliers/api/supplier-products-api';
import { messages } from './edit-supplier-modal.messages';
import styles from './edit-supplier-modal.module.less';

const MAX_SUPPLIER_SKU_LENGTH = 200;

type EditSupplierModalProps = {
  productSupplierGuid: string;
  initialSupplierSku: string;
  initialCost: number;
  initialSupplierName?: string;
  close: () => void;
  onSuccess: () => void;
};

export const EditSupplierModal = ({
  productSupplierGuid,
  initialSupplierSku,
  initialCost,
  initialSupplierName,
  close,
  onSuccess,
}: EditSupplierModalProps) => {
  const intl = useIntl();
  const modalHeaderId = useMemo(() => v4(), []);
  const currencyCode = useSelector(Global.selectSellerDefaultCurrency);

  const [isLoading, setIsLoading] = useState(false);
  const [supplierSku, setSupplierSku] = useState(initialSupplierSku);
  const [cost, setCost] = useState<number>(initialCost);
  const [supplierSkuError, setSupplierSkuError] = useState('');

  const onSupplierSkuChanged = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSupplierSku(event.target.value);
    setSupplierSkuError('');
  }, []);

  const onCostChanged = useCallback((value: string | number | null | undefined) => {
    const costNumber = parseFloat(String(value));
    setCost(isNaN(costNumber) ? 0 : costNumber);
  }, []);

  const handleSave = useCallback(async () => {
    if (supplierSku.length > MAX_SUPPLIER_SKU_LENGTH) {
      setSupplierSkuError(intl.formatMessage(messages.supplierSkuTooLongError, { maxLength: MAX_SUPPLIER_SKU_LENGTH }));
      return;
    }

    setIsLoading(true);
    try {
      await updateSupplierProduct(productSupplierGuid, { supplierSku, cost });
      onSuccess();
      close();
    } catch {
      NotificationService.showNotification({
        title: <FormattedMessage {...messages.errorNotificationTitle} />,
        content: <FormattedMessage {...messages.errorNotificationContent} />,
        notificationType: NOTIFICATION_TYPE_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  }, [productSupplierGuid, supplierSku, cost, onSuccess, close, intl]);

  return (
    <ModalPortal onClose={close}>
      <Modal modalHeaderId={modalHeaderId} size='medium'>
        <ModalHeader closeModal={close} id={modalHeaderId}>
          <Text textType={TextType.H3} value={messages.title} />
        </ModalHeader>
        <ModalBody>
          {isLoading ? (
            <FlexColumn className={styles.loadingContainer}>
              <LoadingIndicator className={styles.loading} />
            </FlexColumn>
          ) : (
            <FlexColumn className={styles.content} gap={Gap.Space16}>
              {initialSupplierName ? (
                <FlexColumn gap={Gap.Space8}>
                  <Text textType={TextType.Label} value={messages.supplierNameLabel} />
                  <Input disabled readOnly value={initialSupplierName} version={version2} />
                </FlexColumn>
              ) : null}
              <FlexColumn gap={Gap.Space8}>
                <Text textType={TextType.Label} value={messages.supplierSkuLabel} />
                <Input
                  inlineMessage={supplierSkuError || undefined}
                  inlineMessageType={supplierSkuError ? INLINE_MESSAGE_TYPES.error : ''}
                  onChange={onSupplierSkuChanged}
                  placeholder={intl.formatMessage(messages.supplierSkuPlaceholder)}
                  value={supplierSku}
                  version={version2}
                />
              </FlexColumn>
              <FlexColumn gap={Gap.Space8}>
                <Text textType={TextType.Label} value={messages.unitCostLabel} />
                <CurrencyInput
                  className={styles.currencyInput}
                  currencyCode={currencyCode}
                  disallowNegative
                  onChange={onCostChanged}
                  value={cost}
                  showPlaceHolderForZero
                />
              </FlexColumn>
            </FlexColumn>
          )}
        </ModalBody>
        <ModalFooter>
          <Button disabled={isLoading} onClick={close} theme={THEME_CONSTANTS.tertiary}>
            <FormattedMessage {...messages.cancel} />
          </Button>
          <Button disabled={isLoading} onClick={handleSave} theme={THEME_CONSTANTS.primary}>
            <FormattedMessage {...messages.save} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};
